import { locales, getLocale, baseLocale } from './runtime';
// @ts-ignore
export const routes = {
  "/category/[...rest]": {
    "sv": "/kategori/[...rest]"
  },
  "/product/[...rest]": {
    "sv": "/produkt/[...rest]"
  },
  "/blog/[...rest]": {
    "sv": "/magazine/[...rest]"
  },
  "/blog/category/[...rest]": {
    "sv": "/magazine/kategori/[...rest]"
  },
  "/checkout/thank-you/[...rest]": {
    "sv": "/kassa/tack/[...rest]"
  },
  "/checkout/[...rest]": {
    "sv": "/kassa/[...rest]"
  },
  "/search/[...rest]": {
    "sv": "/sok/[...rest]"
  },
  "/news/[...rest]": {
    "sv": "/nyheter/[...rest]"
  },
  "/all-brands": {
    "sv": "/alla-varumarken"
  },
  "/brand/[...rest]": {
    "sv": "/varumarke/[...rest]"
  },
  "/use-case": {
    "sv": "/anvandningsomrade"
  },
  "/user": {
    "sv": "/anvandare"
  },
  "/campaigns/[...rest]": {
    "sv": "/kampanjer/[...rest]"
  },
  "/shop-by": {
    "sv": "/shoppa-enligt"
  }
};
// Create lookup tables for faster access
/** @type {Record<string, Record<string, string>>} */
const rawToLocalizedMap = {};
/** @type {Record<string, Record<string, string>>} */
const localizedToRawMap = {};
/** @type {Record<string, RegExp>} */
const patternMap = {};
// TODO: generate this map in the compiler rather then during runtime for more efficiency
// Initialize the lookup tables
const initializeMaps = () => {
    // Process each raw route pattern
    Object.entries(routes).forEach(([rawPattern, localizations]) => {
        // Store raw to localized mapping
        rawToLocalizedMap[rawPattern] = localizations;
        // Create regex pattern for this raw route
        if (rawPattern.includes('[...rest]')) {
            patternMap[rawPattern] = new RegExp(`^${rawPattern.replace(/\/?\[\.\.\.rest\]/g, '(?:/(.*))?')}$`);
        }
        else {
            patternMap[rawPattern] = new RegExp(`^${rawPattern.replace(/\/$/, '')}\\/?$`);
        }
        // Store localized to raw mapping
        Object.entries(localizations).forEach(([locale, localizedPattern]) => {
            if (!localizedToRawMap[locale]) {
                localizedToRawMap[locale] = {};
            }
            localizedToRawMap[locale][localizedPattern] = rawPattern;
        });
    });
};
initializeMaps(); // Initialize on module load
/**
 * Normalizes a URL by removing duplicate slashes
 * @param {URL} url - The URL to normalize
 * @returns {URL} The normalized URL
 */
const normalizeUrl = (url) => {
    url.pathname = normalizePathname(url.pathname);
    return url;
};
/**
 * Normalizes a pathname by removing duplicate slashes
 * @param {string} pathname - The pathname to normalize
 * @returns {string} The normalized pathname
 */
const normalizePathname = (pathname) => {
    return pathname.replace(/\/+/g, '/');
};
/**
 * Get the locale from a URL
 * @param {URL} url - The URL to get the locale from
 * @returns {{ locale: string, flattenedUrl: URL }} The locale and the flattenedUrl URL
 */
export const getLocaleAndFlattenedUrl = (url) => {
    const [locale, ...rest] = url.pathname.split('/').slice(1);
    const flattenedUrl = new URL(url);
    if (locales.includes(locale)) {
        flattenedUrl.pathname = rest.join('/');
    }
    return {
        locale: locales.includes(locale) ? locale : baseLocale,
        flattenedUrl: flattenedUrl
    };
};
/**
 * Localizes a URL path based on the specified locale
 * @param {string} href - The URL to localize
 * @param {string} locale - The locale to localize the URL to
 * @returns {string} The localized URL
 */
export const localizeUrl = (href, locale = getLocale()) => {
    try {
        const newUrl = normalizeUrl(new URL(href));
        // Find matching raw pattern
        for (const [rawPattern, regex] of Object.entries(patternMap)) {
            const match = newUrl.pathname.match(regex);
            if (match) {
                const restParam = match[1] || '';
                const localizationPatterns = rawToLocalizedMap[rawPattern];
                if (localizationPatterns && localizationPatterns[locale]) {
                    const localizedPattern = localizationPatterns[locale];
                    const localizedPath = localizedPattern.replace('[...rest]', restParam);
                    // Set localized path
                    newUrl.pathname = localizedPath;
                    break; // Continue below and add locale path
                }
            }
        }
        // Add the locale path if it's not the base locale
        newUrl.pathname = (locale === baseLocale ? newUrl.pathname : `/${locale}${newUrl.pathname}`).replace(/\/$/, '');
        return newUrl.toString();
    }
    catch (error) {
        // If the URL is invalid, assume it's a path and return as is
        console.error('Error localizing URL:', error);
        return href;
    }
};
/**
 * Converts a localized URL back to its raw (default) version
 * @param {string} href - The URL to localize
 * @returns {string} The localized URL
 */
export const delocalizeUrl = (href) => {
    try {
        // Get a flattenedUrl URL without locale path
        const { flattenedUrl, locale } = getLocaleAndFlattenedUrl(normalizeUrl(new URL(href)));
        const localizedToRawPattern = localizedToRawMap[locale];
        if (localizedToRawPattern) {
            // Try to find an exact match first
            for (const [localizedPattern, rawPattern] of Object.entries(localizedToRawPattern)) {
                // Create regex for the localized pattern
                let localizedRegex;
                if (localizedPattern.includes('[...rest]')) {
                    localizedRegex = new RegExp(`^${localizedPattern.replace(/\[\.\.\.rest\]/g, '?(.*)')}$`);
                }
                else {
                    localizedRegex = new RegExp(`^${localizedPattern.replace(/\/$/, '')}\\/?$`);
                }
                const match = flattenedUrl.pathname.match(localizedRegex);
                if (match) {
                    const restParam = match[1] || '';
                    const rawPath = rawPattern.replace('[...rest]', restParam);
                    // Set raw path
                    flattenedUrl.pathname = rawPath;
                    return flattenedUrl.toString();
                }
            }
        }
        // Return flattenedUrl URL
        return flattenedUrl.toString();
    }
    catch (error) {
        // If the URL is invalid, assume it's a path and return as is
        console.error('Error delocalizing URL:', error);
        return normalizePathname(href);
    }
};
